import React, { useState } from "react";
import { CalendarPlus, Check, Copy, Link2, Video, X } from "lucide-react";
import { useApp } from "../../shared/context/AppContext";

const CreateRoomModal = ({ onClose, onStartCall }) => {
  const { currentUser } = useApp();
  const [roomName, setRoomName] = useState("");
  const [isVideo, setIsVideo] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [createdRoom, setCreatedRoom] = useState(null);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState("options"); // 'options', 'instant', 'later'

  const generateRoomId = () => {
    return `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  };

  const createRoomObject = () => ({
    id: generateRoomId(),
    name: roomName || `Room ${new Date().toLocaleDateString()}`,
    isVideo,
    createdAt: new Date().toISOString(),
    host: {
      id: currentUser?.id,
      username: currentUser?.username,
      email: currentUser?.email,
    },
  });

  const handleCreateForLater = async () => {
    setIsCreating(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));
    const room = createRoomObject();
    setCreatedRoom(room);
    setView("later");
    setIsCreating(false);
  };

  const handleInstantRoom = async (e) => {
    if (e) e.preventDefault();
    setIsCreating(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));
    const room = createRoomObject();
    setCreatedRoom(room);
    setView("instant");
    setIsCreating(false);
  };

  const startCall = () => {
    if (createdRoom && onStartCall) {
      onStartCall(createdRoom);
    }
    onClose();
  };

  const handleGoogleCalendar = () => {
    const room = createRoomObject();
    const link = `${window.location.origin}?room=${room.id}`;
    const title = encodeURIComponent(room.name);
    const details = encodeURIComponent(`Join the room: ${link}`);
    const location = encodeURIComponent(link);

    // Open Google Calendar in new tab
    window.open(
      `https://calendar.google.com/calendar/u/0/r/eventedit?text=${title}&details=${details}&location=${location}`,
      "_blank"
    );
    onClose();
  };

  const copyRoomLink = () => {
    if (createdRoom) {
      const link = `${window.location.origin}?room=${createdRoom.id}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // View: Success Screen (for "Create for later" or "Instant meeting")
  if ((view === "later" || view === "instant") && createdRoom) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-gray-700 animate-slide-up">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">
              Here's your joining info
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <p className="text-gray-400 text-sm mb-4">
            Send this to people you want to meet with. Be sure to save it so you
            can use it later, too.
          </p>

          {/* Room ID */}
          <div className="bg-slate-700/50 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs mb-1">Room ID</p>
                <p className="text-white font-mono text-sm">{createdRoom.id}</p>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(createdRoom.id);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="text-emerald-400 hover:text-emerald-300 p-2"
                title="Copy Room ID"
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Room Link */}
          <div className="bg-slate-700 rounded-lg p-3 flex items-center justify-between mb-6">
            <span className="text-white text-sm truncate mr-4">
              {`${window.location.origin}?room=${createdRoom.id}`}
            </span>
            <button
              onClick={copyRoomLink}
              className="text-emerald-400 hover:text-emerald-300"
              title="Copy link"
            >
              {copied ? (
                <Check className="w-5 h-5" />
              ) : (
                <Link2 className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Host Information */}
          {createdRoom.host && (
            <div className="bg-slate-700/50 rounded-lg p-3 mb-6">
              <p className="text-gray-400 text-xs mb-2">Room Host</p>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {createdRoom.host.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">
                    {createdRoom.host.username}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {createdRoom.host.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Join Now button for instant meetings */}
          {view === "instant" && (
            <button
              onClick={startCall}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold py-3 px-4 rounded-xl transition-colors"
            >
              Join Now
            </button>
          )}
        </div>
      </div>
    );
  }

  // View: Main Options
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-gray-700 animate-slide-up">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">Create Room</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <button
            onClick={handleCreateForLater}
            className="w-full flex items-center p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-all group text-left"
          >
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center mr-4 group-hover:bg-emerald-500/20 transition-colors">
              <Link2 className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">
                Create a room for later
              </h4>
              <p className="text-sm text-gray-400">
                Get a link that you can share
              </p>
            </div>
          </button>

          <button
            onClick={handleInstantRoom}
            className="w-full flex items-center p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-all group text-left"
          >
            <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mr-4 group-hover:bg-cyan-500/20 transition-colors">
              <Video className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Start an instant room</h4>
              <p className="text-sm text-gray-400">Set up a new room now</p>
            </div>
          </button>

          <button
            onClick={handleGoogleCalendar}
            className="w-full flex items-center p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-all group text-left"
          >
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center mr-4 group-hover:bg-emerald-500/20 transition-colors">
              <CalendarPlus className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">
                Schedule in Google Calendar
              </h4>
              <p className="text-sm text-gray-400">Plan a room for later</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRoomModal;
